import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { PlayCircle, Lock, Clock, Award, BookOpen, CheckCircle2, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import MarkdownText from "../components/MarkdownText";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const CATEGORY_LABEL = {
  marketing: "Marketing",
  sales: "Sales",
  service: "Service",
  operations: "Operations",
  "executive-mindset": "Executive Mindset",
};

export default function CourseDetail() {
  const { slug } = useParams();
  const [course, setCourse] = useState(null);
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    setError(null);
    api
      .get(`/courses/${slug}`)
      .then((r) => {
        // Phase 12 — coming-soon courses live on a dedicated waitlist page.
        if (r.data.is_coming_soon) {
          navigate(`/courses/${slug}/coming-soon`, { replace: true });
          return;
        }
        setCourse(r.data);
      })
      .catch((e) => {
        console.error("Failed to load course", e);
        setError(e.response?.status === 404 ? "Course not found" : "Failed to load course");
      })
      .finally(() => setLoading(false));
    // Tools strip — public endpoint, no auth needed
    api.get(`/courses/${slug}/tools`).then((r) => setTools(r.data.tools || [])).catch(() => {});
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="bg-fc-navy min-h-screen text-white">
        <Navbar />
        <div className="pt-32 max-w-5xl mx-auto px-6"><p className="text-white/50">Loading…</p></div>
      </div>
    );
  }
  if (error || !course) {
    return (
      <div className="bg-fc-navy min-h-screen text-white">
        <Navbar />
        <div className="pt-40 max-w-3xl mx-auto px-6 text-center">
          <h1 className="fc-h-section text-3xl mb-4">{error || "Not found"}</h1>
          <Link to="/courses" className="btn-fc-red">Browse all courses</Link>
        </div>
      </div>
    );
  }

  const totalLessons = course.modules.reduce((acc, m) => acc + m.lessons.length, 0);

  return (
    <div className="bg-fc-navy min-h-screen text-white">
      <Navbar />
      <main className="pt-28 pb-32" data-testid="course-detail-page">
        {/* HERO */}
        <section className="bg-[#060A24] border-b border-white/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="fc-overline mb-3">{CATEGORY_LABEL[course.category]}</div>
              <h1 className="fc-h-section text-3xl sm:text-4xl lg:text-5xl xl:text-6xl mb-5" data-testid="course-title">
                {course.title}
              </h1>
              <p className="text-white/70 text-lg leading-relaxed mb-8 max-w-2xl">
                {course.subtitle}
              </p>

              <div className="flex flex-wrap items-center gap-4 mb-8 text-xs tracking-[0.18em] uppercase font-bold">
                <Link to={`/instructors/${course.instructor.id}`} className="flex items-center gap-3 hover:opacity-80" data-testid="instructor-link">
                  <img src={course.instructor.avatar_url} alt={course.instructor.name} className="w-9 h-9 object-cover rounded-full" />
                  <span className="text-white">{course.instructor.name}</span>
                </Link>
                <span className="text-white/40">·</span>
                <span className="text-fc-red">{course.difficulty}</span>
                <span className="text-white/40">·</span>
                <span className="text-white/70 flex items-center gap-1"><Clock size={12} /> {course.estimated_duration_hours}h</span>
                <span className="text-white/40">·</span>
                <span className="text-white/70 flex items-center gap-1"><BookOpen size={12} /> {totalLessons} lessons</span>
              </div>

              <div className="flex flex-wrap gap-3">
                <Link to={`/courses/${course.slug}/preview`} className="btn-fc-red" data-testid="watch-free-lesson-cta">
                  <PlayCircle size={18} /> Watch Free Lesson
                </Link>
                <button onClick={() => navigate(`/register?course=${course.slug}`)} className="btn-fc-outline" data-testid="enroll-cta">
                  Enroll <ArrowRight size={16} />
                </button>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="aspect-[4/3] overflow-hidden border border-white/10">
                <img src={course.thumbnail_url} alt={course.title} className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </section>

        {/* WHAT YOU'LL LEARN */}
        <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="what-you-will-learn">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <div className="fc-overline mb-3">WHAT YOU'LL LEARN</div>
              <h2 className="fc-h-section text-3xl sm:text-4xl mb-8">
                Outcomes, not slides.
              </h2>
              <ul className="grid sm:grid-cols-2 gap-5">
                {course.what_you_will_learn.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-white/85 leading-relaxed" data-testid={`learn-item-${i}`}>
                    <CheckCircle2 className="text-[#10B981] flex-shrink-0 mt-1" size={18} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-white/65 mt-10 leading-relaxed max-w-2xl">
                <MarkdownText variant="dark">{course.sales_description || ""}</MarkdownText>
              </p>
            </div>
            <aside className="lg:col-span-5">
              <div className="bg-[#060A24] border border-white/10 p-6">
                <p className="text-fc-red text-xs tracking-[0.22em] font-bold uppercase mb-3">Includes</p>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-center gap-3 text-white/85"><BookOpen size={14} className="text-fc-red" /> {totalLessons} on-demand lessons</li>
                  <li className="flex items-center gap-3 text-white/85"><Clock size={14} className="text-fc-red" /> {course.estimated_duration_hours} hours of premium video</li>
                  {course.certificate_enabled && (
                    <li className="flex items-center gap-3 text-white/85"><Award size={14} className="text-fc-red" /> Certificate of completion</li>
                  )}
                  <li className="flex items-center gap-3 text-white/85"><Lock size={14} className="text-fc-red" />
                    {course.membership_required === "executive" ? "Executive membership required" : "Staff or Executive membership"}
                  </li>
                </ul>
              </div>
            </aside>
          </div>
        </section>

        {/* MODULES ACCORDION */}
        <section className="bg-[#060A24] py-20 border-y border-white/10">
          <div className="max-w-5xl mx-auto px-6 lg:px-10">
            {tools.length > 0 && (
              <div className="mb-16" data-testid="sales-tools-strip">
                <div className="fc-overline mb-3">TOOLS WE USE</div>
                <h3 className="text-2xl font-black mb-6 max-w-2xl">
                  Built with a <span className="text-fc-red">real-world stack</span>.
                </h3>
                <div className="flex flex-wrap items-center gap-3">
                  {tools.map((t) => (
                    <div
                      key={t.id}
                      className="flex items-center gap-2 bg-fc-navy border border-white/10 px-3 py-2"
                      data-testid={`sales-tool-${t.partner.slug}`}
                      title={t.effective_description}
                    >
                      {t.partner.logo_url && (
                        <div className="w-6 h-6 bg-white p-0.5">
                          <img
                            src={t.partner.logo_url} alt={t.partner.name}
                            className="w-full h-full object-contain"
                            onError={(e) => { e.target.style.display = "none"; }}
                          />
                        </div>
                      )}
                      <span className="text-xs font-bold tracking-wide">{t.partner.name}</span>
                      {t.is_required && (
                        <span className="text-[8px] tracking-[0.18em] uppercase font-black text-fc-red">REQ</span>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-white/45 text-[11px] tracking-wide mt-3">Log in to learn how to use each one inside this course.</p>
              </div>
            )}

            <div className="fc-overline mb-3">CURRICULUM</div>
            <h2 className="fc-h-section text-3xl sm:text-4xl mb-10">
              {course.modules.length} modules. {totalLessons} lessons.
            </h2>
            <Accordion type="multiple" className="space-y-3" data-testid="modules-accordion">
              {course.modules.map((m, mi) => (
                <AccordionItem key={m.id} value={`mod-${mi}`} className="bg-fc-navy border border-white/10 px-6">
                  <AccordionTrigger className="text-left hover:no-underline">
                    <div className="flex flex-col items-start gap-1">
                      <span className="text-[10px] tracking-[0.22em] uppercase text-fc-red font-bold">Module {mi + 1}</span>
                      <span className="text-white font-bold text-lg">{m.title.replace(/^Module \d+: /, "")}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="text-white/60 text-sm mb-4">{m.description}</p>
                    <ul className="divide-y divide-white/10 border-t border-white/10">
                      {m.lessons.map((l) => (
                        <li key={l.id} className="py-3 flex items-center justify-between gap-4" data-testid={`lesson-row-${l.id}`}>
                          <div className="flex items-center gap-3 min-w-0">
                            {l.is_free_preview ? (
                              <PlayCircle size={16} className="text-fc-red flex-shrink-0" />
                            ) : (
                              <Lock size={14} className="text-white/35 flex-shrink-0" />
                            )}
                            <span className="text-white/90 text-sm truncate">{l.title}</span>
                          </div>
                          <div className="flex items-center gap-4 flex-shrink-0">
                            <span className="text-white/40 text-xs">{l.duration_minutes} min</span>
                            {l.is_free_preview && (
                              <Link
                                to={`/courses/${course.slug}/preview`}
                                className="text-[10px] tracking-[0.18em] uppercase font-bold text-fc-red hover:underline"
                                data-testid="lesson-free-badge"
                              >
                                FREE →
                              </Link>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* INSTRUCTOR */}
        <section className="max-w-5xl mx-auto px-6 lg:px-10 py-20">
          <div className="fc-overline mb-3">YOUR INSTRUCTOR</div>
          <Link to={`/instructors/${course.instructor.id}`} className="block">
            <div className="grid sm:grid-cols-[auto_1fr] gap-8 items-start bg-[#060A24] border border-white/10 p-8 hover:border-fc-red/40 transition-colors">
              <img src={course.instructor.avatar_url} alt={course.instructor.name} className="w-28 h-28 object-cover" />
              <div>
                <h3 className="text-2xl font-black mb-2">{course.instructor.name}</h3>
                <p className="text-fc-red text-xs tracking-[0.2em] uppercase font-bold mb-4">{course.instructor.title}</p>
                <p className="text-white/70 leading-relaxed mb-3">{course.instructor.bio}</p>
                <p className="text-xs text-white/45 tracking-wider">{course.instructor.credentials?.join(" · ")}</p>
              </div>
            </div>
          </Link>
        </section>
      </main>

      {/* STICKY MOBILE CTA */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[#060A24] border-t border-white/10 p-3 flex gap-2" data-testid="mobile-sticky-cta">
        <Link to={`/courses/${course.slug}/preview`} className="btn-fc-red flex-1 justify-center text-xs py-3">
          <PlayCircle size={16} /> Free Lesson
        </Link>
        <button onClick={() => navigate(`/register?course=${course.slug}`)} className="btn-fc-outline flex-1 justify-center text-xs py-3">
          Enroll
        </button>
      </div>

      <Footer />
    </div>
  );
}
